// src/pages/dashboard/branch/analytics/components/ItemPerformanceTable.tsx
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FiPackage, FiAward } from "react-icons/fi";
import Card from "../../../../../components/ui/Card";
import Skeleton from "../../../../../components/ui/Skeleton";
import { MenuItem } from "../../../../../utils/supabaseClient";
import { useLocalizedMenuItem } from "../../../../../hooks/useLocalizedMenuItem";
import { useLocalizedCategory } from "../../../../../hooks/useLocalizedCategory";

interface ItemSummary {
    item_id: string;
    totalRevenue: number;
    totalOrders: number;
    avgOrderValue: number;
}

interface Category {
    id: string;
    name: string;
}

interface ItemPerformanceTableProps {
    itemsSummary: ItemSummary[];
    menuItemsMap: Map<string, MenuItem>;
    categories: Category[];
    isLoading?: boolean;
    formatCurrency?: (value: number) => string;
    branchId: string;
}

const CHART_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6"];

const TableSkeleton: React.FC = () => (
    <Card>
        <div className="flex items-center gap-2 mb-6">
            <Skeleton height="h-5" width="w-5" rounded="rounded" />
            <Skeleton height="h-6" width="w-48" />
        </div>
        <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <Skeleton height="h-2" width="w-2" rounded="rounded-full" />
                    <Skeleton height="h-10" width="w-10" rounded="rounded-lg" />
                    <div className="flex-1">
                        <Skeleton height="h-4" width="w-32" className="mb-2" />
                        <Skeleton height="h-3" width="w-20" />
                    </div>
                    <Skeleton height="h-4" width="w-16" />
                    <Skeleton height="h-4" width="w-20" />
                    <Skeleton height="h-4" width="w-20" />
                </div>
            ))}
        </div>
    </Card>
);

const ItemPerformanceRow = memo(({
    item,
    idx,
    branchId,
    maxRevenue,
    categories,
    formatCurrency
}: {
    item: ItemSummary;
    idx: number;
    branchId: string;
    maxRevenue: number;
    categories: Category[];
    formatCurrency?: (value: number) => string;
}) => {
    const { t } = useTranslation();
    const { localizedName, menuItem } = useLocalizedMenuItem(branchId, item.item_id);
    const { localizedName: localizedCategoryName } = useLocalizedCategory(branchId, menuItem?.category_id);
    const category = categories.find(cat => cat.id === menuItem?.category_id);
    const revenuePercent = maxRevenue > 0 ? (item.totalRevenue / maxRevenue) * 100 : 0;
    const color = CHART_COLORS[idx % CHART_COLORS.length];

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: idx * 0.05 }}
            className="relative group grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 rounded-xl transition-all duration-200 border border-transparent hover:border-primary/20 overflow-hidden"
        >
            {/* Background progress bar */}
            <div
                className="absolute left-0 top-0 bottom-0 opacity-5 transition-all duration-500"
                style={{
                    width: `${revenuePercent}%`,
                    backgroundColor: color,
                }}
            />

            {/* Rank indicator */}
            {idx < 3 && (
                <div className="absolute top-2 right-2">
                    <span className={`
                        inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                        ${idx === 0 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" : ""}
                        ${idx === 1 ? "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300" : ""}
                        ${idx === 2 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : ""}
                    `}>
                        {idx + 1}
                    </span>
                </div>
            )}

            {/* Item Info */}
            <div className="relative col-span-5 flex items-center gap-3">
                <div
                    className="w-1.5 h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                />
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 group-hover:ring-2 group-hover:ring-primary/30 transition-all">
                    {menuItem?.image_url ? (
                        <img
                            src={menuItem.image_url}
                            alt={localizedName || "Item"}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <FiPackage className="w-5 h-5 text-gray-400" />
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                        {localizedName || `${t("analytics.itemPerformanceTable.item")} ${item.item_id.slice(0, 8)}`}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {localizedCategoryName || category?.name || t("analytics.itemPerformanceTable.unknownCategory")}
                    </p>
                </div>
            </div>

            {/* Total Orders */}
            <div className="relative col-span-2 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {item.totalOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 md:hidden">{t("analytics.itemPerformanceTable.orders")}</p>
                </div>
            </div>

            {/* Total Revenue */}
            <div className="relative col-span-3 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <p className="text-lg font-bold text-emerald-500">
                        {formatCurrency ? formatCurrency(item.totalRevenue) : `$${item.totalRevenue.toFixed(2)}`}
                    </p>
                    <p className="text-xs text-gray-400 md:hidden">{t("analytics.itemPerformanceTable.revenue")}</p>
                </div>
            </div>

            {/* Avg Order Value */}
            <div className="relative col-span-2 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                        {formatCurrency ? formatCurrency(item.avgOrderValue) : `$${item.avgOrderValue.toFixed(2)}`}
                    </p>
                    <p className="text-xs text-gray-400 md:hidden">{t("analytics.itemPerformanceTable.avg")}</p>
                </div>
            </div>
        </motion.div>
    );
});
ItemPerformanceRow.displayName = "ItemPerformanceRow";

const ItemPerformanceTable: React.FC<ItemPerformanceTableProps> = memo(({
    itemsSummary,
    menuItemsMap, // No longer used directly for rendering, but kept for interface compatibility or if needed
    categories,
    isLoading = false,
    formatCurrency,
    branchId,
}) => {
    const { t } = useTranslation();

    if (isLoading) {
        return <TableSkeleton />;
    }

    if (!itemsSummary || itemsSummary.length === 0) {
        return (
            <Card>
                <div className="flex items-center gap-2 mb-4">
                    <FiPackage className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("analytics.itemPerformanceTable.title")}
                    </h3>
                </div>
                <div className="flex flex-col items-center justify-center py-12">
                    <FiPackage className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                        {t("analytics.itemPerformanceTable.selectItemsPrompt")}
                    </p>
                </div>
            </Card>
        );
    }

    const maxRevenue = Math.max(...itemsSummary.map(item => item.totalRevenue));

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
        >
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <FiAward className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t("analytics.itemPerformanceTable.title")}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t("analytics.itemPerformanceTable.rankedByRevenue")}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <div className="col-span-5">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemPerformanceTable.item")}
                        </span>
                    </div>
                    <div className="col-span-2 text-center">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemPerformanceTable.totalOrders")}
                        </span>
                    </div>
                    <div className="col-span-3 text-center">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemPerformanceTable.totalRevenue")}
                        </span>
                    </div>
                    <div className="col-span-2 text-center">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemPerformanceTable.avgValue")}
                        </span>
                    </div>
                </div>

                {/* Table Rows */}
                <div className="space-y-2">
                    <AnimatePresence>
                        {itemsSummary.map((item, idx) => (
                            <ItemPerformanceRow
                                key={item.item_id}
                                item={item}
                                idx={idx}
                                branchId={branchId}
                                maxRevenue={maxRevenue}
                                categories={categories}
                                formatCurrency={formatCurrency}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            </Card>
        </motion.div>
    );
});

ItemPerformanceTable.displayName = "ItemPerformanceTable";

export default ItemPerformanceTable;
